import { useTranslation } from 'react-i18next';
import { Divider } from '@treezor/design-system';

import { SummaryInformation } from '@/ui-kit/SummaryInformation/SummaryInformation';

import { CreateUserFooter } from '@/features/user/components/CreateUser/layout/CreateUserFooter';
import { AddressSummary } from '@/features/user/components/CreateUser/AddressSummary';
import { TaxResidenceSummary } from '@/features/user/components/CreateUser/TaxResidenceSummary';
import { KycDocumentSummary } from '@/features/user/components/CreateUser/KycDocumentSummary';
import { SanctionEmbargoSummary } from '@/features/user/components/CreateUser/SanctionEmbargoSummary';
import { PersonalSituationSummary } from '@/features/user/components/CreateUser/PersonalSituationSummary';
import { useCreateSelfEmployedUser } from '@/features/user/hooks/createUser/useCreateSelfEmployedUser';
import { legalForms } from '@/features/user/store/user.model';

export interface ICreateUserSelfSummaryProps {
  stepsLength: number;
}

export function CreateUserSelfSummary({ stepsLength }: ICreateUserSelfSummaryProps) {
  const { t } = useTranslation();

  const { config, distributionCountry, user, onConfirmCreation, isLoading, legalFormValue } =
    useCreateSelfEmployedUser();

  return (
    <div className="flex flex-col gap-6">
      <SummaryInformation
        title={t('pages.dashboard.createUser.form.pages.userInformation')}
        sections={[
          {
            subTitle: t('pages.dashboard.createUser.form.blocks.mainInformation'),
            categories: [
              {
                items: [
                  { label: t('pages.dashboard.createUser.form.title'), value: user?.title || '-' },
                  { label: t('pages.dashboard.createUser.form.lastname'), value: user.lastname },
                  {
                    label: t('pages.dashboard.createUser.form.firstname'),
                    value: [user.firstname, user.middleName1, user.middleName2].filter(Boolean).join(', '),
                  },
                  { label: t('pages.dashboard.createUser.form.email'), value: user.email },
                  distributionCountry?.length > 0 && {
                    label: t('pages.currentProfiles.profile.informations.distributionCountry'),
                    value: t(`shared.countries.${user.distributionCountry}`),
                  },
                ],
              },
              {
                items: [
                  {
                    label: t('pages.dashboard.createUser.form.sirenLegalRegistrationNumber'),
                    value: user?.legalRegistrationNumber,
                  },
                  { label: t('pages.dashboard.createUser.form.legalName'), value: user?.legalName || '-' },
                  {
                    label: t('pages.dashboard.createUser.form.legalRegistrationDate'),
                    value: user?.legalRegistrationDate || '-',
                  },
                  {
                    label: t('pages.dashboard.createUser.form.legalForm'),
                    value: `${legalFormValue} - ${legalForms[legalFormValue]}`,
                  },
                  { label: t('pages.dashboard.createUser.form.legalTvaNumber'), value: user?.legalTvaNumber || '-' },
                ],
              },
              {
                items: [
                  {
                    label: t('pages.dashboard.createUser.form.legalSector'),
                    value: `${user?.legalSectorType} - ${user?.legalSector}`,
                  },
                ],
              },
            ],
          },
          {
            subTitle: t('pages.dashboard.createUser.form.blocks.address'),
            categories: [
              {
                component: (
                  <div className="flex gap-8">
                    <AddressSummary
                      isPrimary
                      title={t('pages.dashboard.createUser.selfEmployed.sections.professionalAddress.title')}
                      address1={user.address1}
                      address2={user.address2}
                      address3={user.address3}
                      postcode={user.postcode}
                      city={user.city}
                      country={user.country}
                    />

                    <AddressSummary
                      title={t('pages.dashboard.createUser.selfEmployed.sections.personnalAddress')}
                      address1={user.secondaryAddress1}
                      address2={user.secondaryAddress2}
                      address3={user.secondaryAddress3}
                      postcode={user.secondaryPostcode}
                      city={user.secondaryCity}
                      country={user.secondaryCountry}
                    />
                  </div>
                ),
              },
            ],
          },
          {
            subTitle: t('pages.dashboard.createUser.form.blocks.contact'),
            categories: [
              {
                items: [
                  { label: t('pages.dashboard.createUser.form.phone'), value: user?.phone || '-' },
                  { label: t('pages.dashboard.createUser.form.mobile'), value: user?.mobile || '-' },
                ],
              },
            ],
          },
        ]}
      />
      <SummaryInformation
        title={t('pages.dashboard.createUser.form.pages.verificationDetails')}
        sections={[
          {
            subTitle: t('pages.dashboard.createUser.form.blocks.birthNationality'),
            categories: [
              {
                items: [
                  { label: t('pages.dashboard.createUser.form.birthday'), value: user?.birthday || '-' },
                  {
                    label: t('pages.dashboard.createUser.form.birthCountry'),
                    value: user?.birthCountry ? t(`shared.countries.${user.birthCountry}`) : '-',
                  },
                  { label: t('pages.dashboard.createUser.form.placeOfBirth'), value: user.placeOfBirth || '-' },
                  {
                    label: t('pages.dashboard.createUser.form.nationalities'),
                    value:
                      [user?.nationality, user?.nationalityOther]
                        .filter(Boolean)
                        .map((nationality) => t(`shared.countries.${nationality}`))
                        .join(', ') || '-',
                  },
                ],
              },
            ],
          },
          {
            subTitle: t('pages.dashboard.createUser.form.blocks.situation'),
            categories: [
              {
                ...(config?.settings?.users?.dataQuality?.includes('personalSituation')
                  ? {
                      component: (
                        <PersonalSituationSummary
                          occupationCategory={user.occupationCategory}
                          monthlyIncomeRange={user.monthlyIncomeRange}
                          personalAssetsRange={user.personalAssetsRange}
                        />
                      ),
                    }
                  : {
                      items: [
                        {
                          label: t('pages.dashboard.createUser.form.personalAssets.title'),
                          value: user?.personalAssets
                            ? t(`pages.dashboard.createUser.form.personalAssets.${user.personalAssets}`)
                            : '-',
                        },
                        {
                          label: t('pages.dashboard.createUser.form.incomeRange.title'),
                          value: user?.incomeRange
                            ? t(`pages.dashboard.createUser.form.incomeRange.${user.incomeRange}`)
                            : '-',
                        },
                        {
                          label: t('pages.currentProfiles.profile.financial.occupationType'),
                          value: user?.occupationType
                            ? t(`pages.currentProfiles.profile.financial.occupationTypes.${user.occupationType}`)
                            : '-',
                        },
                      ],
                    }),
              },
              {
                items: [
                  {
                    label: t('pages.dashboard.createUser.form.legalAnnualTurnOver'),
                    value: user.legalAnnualTurnOver
                      ? t(`pages.dashboard.createUser.form.legalAnnualTurnOvers.${user.legalAnnualTurnOver}`)
                      : '-',
                  },
                  {
                    label: t('pages.dashboard.createUser.form.legalNetIncomeRanges.title'),
                    value: user.legalNetIncomeRange
                      ? t(`pages.dashboard.createUser.form.legalNetIncomeRanges.${user.legalNetIncomeRange}`)
                      : '-',
                  },
                  {
                    label: t('pages.dashboard.createUser.form.legalNumberOfEmployee'),
                    value: user?.legalNumberOfEmployeeRange || '-',
                  },
                ],
              },
              {
                component: (
                  <SanctionEmbargoSummary
                    entitySanctionsQuestionnaire={user?.entitySanctionsQuestionnaire}
                    activityOutsideEu={user?.activityOutsideEu}
                    economicSanctions={user?.economicSanctions}
                    residentCountriesSanctions={user?.residentCountriesSanctions}
                    involvedSanctions={user?.involvedSanctions}
                  />
                ),
              },
            ],
          },
          {
            subTitle: t('pages.currentProfiles.profile.taxResidence.cardTitle'),
            categories: [
              {
                items: [
                  {
                    label: t('pages.dashboard.createUser.form.usTaxResident'),
                    value:
                      Number(user?.specifiedUSPerson || 0) === 1
                        ? t('translation:components.molecules.forms.yes')
                        : t('translation:components.molecules.forms.no'),
                  },
                ],
              },
              {
                component: <TaxResidenceSummary taxResidences={user?.taxResidences} />,
              },
            ],
          },
          ...(user?.kycDocuments?.length > 0
            ? [
                {
                  subTitle: t('pages.dashboard.createUser.form.blocks.documents'),
                  categories: [
                    {
                      component: <KycDocumentSummary documents={user.kycDocuments} />,
                    },
                  ],
                },
              ]
            : []),
        ]}
      />
      <Divider />
      <CreateUserFooter
        onNext={onConfirmCreation}
        nextTitle={t('pages.dashboard.createUser.form.actions.confirmCreation')}
        isLoading={isLoading}
        stepsLength={stepsLength}
      />
    </div>
  );
}
